/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, concat, concatMap, Observable, of, retry, throwError } from 'rxjs';
import { UserService } from './user.service';
import { first, tap } from 'rxjs/operators';
import { BadInput, NotFoundError } from '@easy-lib/ErrorHandler';
import { EasyEnvironment } from '@easy-lib/modules/shared/models/environment';

export type HttpExtra = {
	options?: httpOptions;
	body?: any | null;
	maxRetryAttempts?: number;
	delayMs?: number;
	dontCheckSameLogin?: boolean;
	guestUserRestriction?: boolean;
};

type RestrictError = { status: number; error?: string };
@Injectable({
	providedIn: 'root'
})
export class HttpService {
	private environment: EasyEnvironment = inject(EasyEnvironment);
	private http: HttpClient = inject(HttpClient);
	private userService: UserService = inject(UserService);
	public get restrictionError(): RestrictError {
		return { status: 400, error: 'دسترسی غیر مجاز' };
	}

	getCached<T>(localStorageKey: string, url: string, httpExtra?: HttpExtra): Observable<T> {
		return concat(
			this.getCachedFromLocalStorage(localStorageKey),
			this.getFromApiAndSetToLocalStorage(localStorageKey, url, httpExtra)
		);
	}

	get<T>(url: string, httpExtra?: HttpExtra): Observable<T> {
		return this.httpGet<T>(url, httpExtra).pipe(
			retry({
				count: httpExtra?.maxRetryAttempts ?? 0,
				delay: httpExtra?.delayMs ?? 1000
			}),
			catchError(
				error => this.handleError(error)
				// return of(null);
			)
		);
	}

	getBlob(
		url: string,
		options: {
			headers?:
				| HttpHeaders
				| {
						[header: string]: string | string[];
				  };
			context?: HttpContext;
			observe?: 'body';
			params?:
				| HttpParams
				| {
						[param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
				  };
			reportProgress?: boolean;
			responseType: 'blob';
			withCredentials?: boolean;
		},
		maxRetryAttempts = 0,
		delayMs = 1000
	): Observable<any> {
		return this.http.get(url, options).pipe(
			retry({
				count: maxRetryAttempts,
				delay: delayMs
			}),
			catchError(
				error => this.handleError(error)
				// return of(null);
			)
		);
	}

	post<T>(url: string, httpExtra?: HttpExtra): Observable<T> {
		return this.httpPost<T>(url, httpExtra?.body, httpExtra).pipe(
			retry({
				count: httpExtra?.maxRetryAttempts ?? 0,
				delay: httpExtra?.delayMs ?? 1000
			}),
			catchError(
				error => this.handleError(error)
				// return of(null);
			)
		);
	}

	put<T>(url: string, httpExtra: HttpExtra): Observable<T> {
		return this.httpPut<T>(url, httpExtra?.body, httpExtra).pipe(
			retry({
				count: httpExtra?.maxRetryAttempts ?? 0,
				delay: httpExtra?.delayMs ?? 1000
			}),
			catchError(
				error => this.handleError(error)
				// return of(null);
			)
		);
	}

	delete<T>(url: string, httpExtra?: HttpExtra): Observable<T> {
		return this.httpDelete<T>(url, httpExtra?.body, httpExtra).pipe(
			retry({
				count: httpExtra?.maxRetryAttempts ?? 0,
				delay: httpExtra?.delayMs ?? 1000
			}),
			catchError(
				error => this.handleError(error)
				// return of(null);
			)
		);
	}

	private httpGet<T>(url: string, extra?: HttpExtra): Observable<T> {
		if (this.checkGuestUserRestriction(extra)) return of([] as T);
		if (extra?.dontCheckSameLogin) return this.http.get<T>(url, extra?.options);
		return this.userService.userChecked$.pipe(
			first(res => res),
			concatMap(() => this.http.get<T>(url, extra?.options))
		);
	}

	private httpPost<T>(url: string, body: any | null, extra?: HttpExtra): Observable<T> {
		if (this.checkGuestUserRestriction(extra)) return this.handleError(this.restrictionError);
		if (extra?.dontCheckSameLogin) return this.http.post<T>(url, body, extra?.options);
		return this.userService.userChecked$.pipe(
			first(res => res),
			concatMap(() => this.http.post<T>(url, body, extra?.options))
		);
	}

	private httpPut<T>(url: string, body: any | null, extra?: HttpExtra): Observable<T> {
		if (this.checkGuestUserRestriction(extra)) return this.handleError(this.restrictionError);
		if (extra?.dontCheckSameLogin) this.http.put<T>(url, body, extra?.options);
		return this.userService.userChecked$.pipe(
			first(res => res),
			concatMap(() => this.http.put<T>(url, body, extra?.options))
		);
	}

	private httpDelete<T>(url: string, body?: any | null, extra?: HttpExtra): Observable<T> {
		if (this.checkGuestUserRestriction(extra)) return this.handleError(this.restrictionError);
		const newOptions = { ...extra?.options, body };
		if (extra?.dontCheckSameLogin) this.http.delete<T>(url, newOptions);

		return this.userService.userChecked$.pipe(
			first(res => res),
			concatMap(() => this.http.delete<T>(url, newOptions))
		);
	}
	checkGuestUserRestriction(extra?: HttpExtra) {
		return extra?.guestUserRestriction && this.userService.isGuestUser;
	}
	private getCachedFromLocalStorage(key: string): Observable<any> {
		const data = localStorage.getItem(key);
		return of(data ? JSON.parse(data) : null);
	}

	private getFromApiAndSetToLocalStorage(key: string, url: string, httpExtra: HttpExtra): Observable<any> {
		return this.get(url, httpExtra).pipe(
			tap(data => {
				localStorage.setItem(key, JSON.stringify(data));
			}),
			catchError(() => this.getCachedFromLocalStorage(key))
		);
	}

	public handleError(error: any): Observable<any> {
		if (!this.environment.production) {
			console.log(`Error ${error.status}:`, error);
		}

		if (error.status === 400) {
			if (error.error?.errors) {
				let message = '';
				Object.keys(error.error.errors).forEach(object => {
					error.error.errors[object].forEach(element => {
						message += element + '، ';
					});
				});
				return throwError(() => new BadInput(message.slice(0, -2)));
			}
			const message = error.error?.detail ?? error.error;
			return throwError(() => new BadInput(message));
		}

		if (error.status === 404) {
			return throwError(() => new NotFoundError(error.error));
		}

		if (error.status === 502) {
			//this.nextApiUrl();
		}
		return throwError(() => error);
	}
}

type httpOptions = {
	headers?:
		| HttpHeaders
		| {
				[header: string]: string | string[];
		  };
	context?: HttpContext;
	observe?: 'body';
	params?:
		| HttpParams
		| {
				[param: string]: string | number | boolean | ReadonlyArray<string | number | boolean>;
		  };
	reportProgress?: boolean;
	responseType?: 'json';
	withCredentials?: boolean;
};
